import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Spin, Empty, Tag, Progress } from "antd";
import {
  ArrowUpOutlined,
  ArrowDownOutlined,
  MinusOutlined,
} from "@ant-design/icons";

import styles from "./ViewClientDiagnosisTab.module.scss";
import { getClientDiagnosis } from "../../api/Services";
import { ClientDiagnosis } from "../../types/clientDiagnosis";

interface Props {
  clientId: string;
}

const formatCurrency = (value: number) =>
  `$ ${Math.round(value || 0).toLocaleString("es-AR")}`;

const formatNumber = (value: number) =>
  (value || 0).toLocaleString("es-AR", { maximumFractionDigits: 1 });

const ViewClientDiagnosisTab = ({ clientId }: Props) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<ClientDiagnosis | null>(null);

  useEffect(() => {
    let mounted = true;
    const fetchData = async () => {
      setLoading(true);
      const response = await getClientDiagnosis(clientId);
      if (mounted) {
        setData(response);
        setLoading(false);
      }
    };
    fetchData();
    return () => {
      mounted = false;
    };
  }, [clientId]);

  if (loading) {
    return (
      <div className={styles.centered}>
        <Spin size="large" />
      </div>
    );
  }

  if (!data) {
    return (
      <div className={styles.centered}>
        <Empty description={t("ClientDiagnosis-NoData")} />
      </div>
    );
  }

  const stateLabel = data.status.state
    ? t(`ClientDiagnosis-State-${data.status.state}`)
    : "-";

  const renderVariationTag = (variationPct: number | null) => {
    if (variationPct == null) {
      return <span className={styles.metricVariationNeutral}>-</span>;
    }
    const positive = variationPct > 0;
    const negative = variationPct < 0;
    const className = positive
      ? styles.metricVariationUp
      : negative
      ? styles.metricVariationDown
      : styles.metricVariationNeutral;
    const Icon = positive
      ? ArrowUpOutlined
      : negative
      ? ArrowDownOutlined
      : MinusOutlined;
    return (
      <span className={className}>
        <Icon /> {Math.abs(variationPct)}%
      </span>
    );
  };

  const recencyDirectionClass = (direction: string) => {
    if (direction === "better") return styles.metricVariationUp;
    if (direction === "stable") return styles.metricVariationNeutral;
    return styles.metricVariationWarning;
  };

  const trendWindowLabel = (window: string) =>
    t(`ClientDiagnosis-Window-${window}`);

  const incentiveBucketLabel = (type: string) =>
    t(`ClientDiagnosis-Incentive-${type}`);

  const readingLabel = (reading: string) =>
    t(`ClientDiagnosis-Reading-${reading}`);

  const insightText = (key: string, params: Record<string, string | number>) =>
    t(key, params as Record<string, unknown>);

  return (
    <div className={styles.wrapper}>
      <h1 className={styles.title}>{t("Common-ClientDiagnosis")}</h1>

      <div className={styles.kpiRow}>
        <div className={`${styles.kpiCard} ${styles.kpiState}`}>
          <span className={styles.kpiLabel}>{t("ClientDiagnosis-CurrentState")}</span>
          <span className={styles.kpiValue}>{stateLabel}</span>
          {data.status.reactivated && (
            <Tag color="green" className={styles.reactivatedTag}>
              {t("ClientDiagnosis-Reactivated")}
            </Tag>
          )}
        </div>
        <div className={`${styles.kpiCard} ${styles.kpiValueCard}`}>
          <span className={styles.kpiLabel}>{t("ClientDiagnosis-HistoricValue")}</span>
          <span className={styles.kpiValue}>{formatCurrency(data.historicValue)}</span>
        </div>
        <div className={`${styles.kpiCard} ${styles.kpiLevel}`}>
          <span className={styles.kpiLabel}>{t("ClientDiagnosis-Level")}</span>
          <span className={styles.kpiValue}>
            {data.hasBenefitsProgram ? data.level || "-" : ""}
          </span>
        </div>
      </div>

      <section className={styles.block}>
        <h2 className={styles.blockTitle}>
          {t("ClientDiagnosis-BehaviorChanges")}
        </h2>
        <div className={styles.behaviorRow}>
          <div className={styles.behaviorCard}>
            <span className={styles.behaviorLabel}>
              {t("ClientDiagnosis-Frequency")}
            </span>
            <div className={styles.behaviorValue}>
              {renderVariationTag(data.behaviorChanges.frequency.variationPct)}
            </div>
            <span className={styles.behaviorHint}>
              {t("ClientDiagnosis-FrequencyHint")}
            </span>
          </div>
          <div className={styles.behaviorCard}>
            <span className={styles.behaviorLabel}>
              {t("ClientDiagnosis-Ticket")}
            </span>
            <div className={styles.behaviorValue}>
              {renderVariationTag(data.behaviorChanges.ticket.variationPct)}
            </div>
            <span className={styles.behaviorHint}>
              {t("ClientDiagnosis-TicketHint")}
            </span>
          </div>
          <div className={styles.behaviorCard}>
            <span className={styles.behaviorLabel}>
              {t("ClientDiagnosis-Recency")}
            </span>
            <div
              className={`${styles.behaviorValue} ${recencyDirectionClass(
                data.behaviorChanges.recency.direction
              )}`}
            >
              {t(
                `ClientDiagnosis-RecencyDirection-${data.behaviorChanges.recency.direction}`
              )}
            </div>
            <span className={styles.behaviorHint}>
              {t("ClientDiagnosis-RecencyHint", {
                days: data.behaviorChanges.recency.recencyDays,
              })}
            </span>
          </div>
        </div>

        <div className={styles.trendWrapper}>
          <h3 className={styles.trendTitle}>{t("ClientDiagnosis-Trend")}</h3>
          <div className={styles.trendGrid}>
            {data.trend.map((row) => (
              <div className={styles.trendCard} key={row.window}>
                <span className={styles.trendWindow}>
                  {trendWindowLabel(row.window)}
                </span>
                <div className={styles.trendItem}>
                  <span>{t("ClientDiagnosis-Frequency")}</span>
                  <span className={styles.trendItemValue}>
                    {t("ClientDiagnosis-PurchasesValue", {
                      value: formatNumber(row.monthlyFrequency),
                    })}
                  </span>
                </div>
                <div className={styles.trendItem}>
                  <span>{t("ClientDiagnosis-Ticket")}</span>
                  <span className={styles.trendItemValue}>
                    {formatCurrency(row.avgTicket)}
                  </span>
                </div>
                <div className={styles.trendItem}>
                  <span>{t("ClientDiagnosis-Recency")}</span>
                  <span className={styles.trendItemValue}>
                    {row.recencyDays != null
                      ? t("ClientDiagnosis-DaysValue", { days: row.recencyDays })
                      : "-"}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className={styles.block}>
        <h2 className={styles.blockTitle}>
          {t("ClientDiagnosis-PurchasePattern")}
        </h2>

        <div className={styles.patternSubCard}>
          <span className={styles.patternLabel}>
            {t("ClientDiagnosis-Cadence")}
          </span>
          <span className={styles.patternValue}>
            {data.purchasePattern.cadenceDays != null
              ? t("ClientDiagnosis-CadenceText", {
                  days: data.purchasePattern.cadenceDays,
                })
              : "-"}
          </span>
        </div>

        <div className={styles.patternSubCard}>
          <span className={styles.patternLabel}>
            {t("ClientDiagnosis-TopCategories")}
          </span>
          <div className={styles.categoryTags}>
            {data.purchasePattern.topCategories.length > 0 ? (
              data.purchasePattern.topCategories.map((category, index) => (
                <Tag key={category.id ?? index} className={styles.categoryTag}>
                  {category.name || "-"} ({formatNumber(category.participationPct)}%)
                </Tag>
              ))
            ) : (
              <span className={styles.patternEmpty}>-</span>
            )}
          </div>
        </div>

        <div className={styles.patternSubCard}>
          <span className={styles.patternLabel}>
            {t("ClientDiagnosis-MainChannel")}
          </span>
          {data.purchasePattern.mainChannel ? (
            <>
              <span className={styles.patternValue}>
                {data.purchasePattern.mainChannel.name || "-"}
              </span>
              <span className={styles.patternHint}>
                {t("ClientDiagnosis-ChannelShare", {
                  pct: formatNumber(
                    data.purchasePattern.mainChannel.participationPct
                  ),
                })}
              </span>
            </>
          ) : (
            <span className={styles.patternEmpty}>-</span>
          )}
          {data.purchasePattern.stores.length > 0 && (
            <div className={styles.storeTags}>
              {data.purchasePattern.stores.map((store, index) => (
                <Tag key={store.id ?? index} className={styles.storeTag}>
                  {store.name || "-"} ({formatNumber(store.participationPct)}%)
                </Tag>
              ))}
            </div>
          )}
        </div>
      </section>

      <section className={styles.block}>
        <h2 className={styles.blockTitle}>
          {t("ClientDiagnosis-IncentiveSensitivity")}
        </h2>
        {data.incentiveSensitivity.hasParticipation &&
        data.incentiveSensitivity.buckets.length > 0 ? (
          <div className={styles.incentiveList}>
            {data.incentiveSensitivity.buckets.map((bucket) => (
              <div className={styles.incentiveItem} key={bucket.type}>
                <div className={styles.incentiveHeader}>
                  <span className={styles.incentiveType}>
                    {incentiveBucketLabel(bucket.type)}
                  </span>
                  <span className={styles.incentiveRate}>
                    {formatNumber(bucket.responseRate)}%
                  </span>
                </div>
                <Progress
                  percent={bucket.responseRate}
                  showInfo={false}
                  strokeColor="#1d2433"
                  trailColor="#e8eaef"
                />
                <span className={styles.incentiveReading}>
                  {readingLabel(bucket.reading)}
                </span>
              </div>
            ))}
          </div>
        ) : (
          <Empty description={t("ClientDiagnosis-NoCampaigns")} />
        )}
      </section>

      <section className={styles.block}>
        <h2 className={styles.blockTitle}>
          {t("ClientDiagnosis-AutomaticInsights")}
        </h2>
        {data.insights.length > 0 ? (
          <div className={styles.insightGrid}>
            {data.insights.map((insight, index) => (
              <div className={styles.insightCard} key={insight.key}>
                <span className={styles.insightIndex}>{index + 1}</span>
                <span className={styles.insightText}>
                  {insightText(insight.key, insight.params)}
                </span>
              </div>
            ))}
          </div>
        ) : (
          <Empty description={t("ClientDiagnosis-NoInsights")} />
        )}
      </section>
    </div>
  );
};

export default ViewClientDiagnosisTab;
